import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { globalEnv } from "~/global-env";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import {
  useCreateEntityClientOnboardingCaseMutation,
  useGetProgramNamesAndIdsQuery,
  useOnboardingCaseForSubjectLazyQuery,
} from "~/graphql/supergraphHooks";
import {
  isUnauthorizedWorkspaceError,
  UNAUTHORIZED_WORKSPACE_TOAST,
} from "~/pages/Compliance/utils/unauthorizedWorkspace";
import { buildLinkExternalObject } from "../CaseInformationStep/buildExternalObject";
import { useEntityClientWizard } from "../context";
import {
  buildExternalArena,
  buildExternalEntity,
  buildInstitutionProfileInput,
  isLinkedOnboardingSubject,
  missingProgramToastMessage,
  resolveOnboardingCaseType,
} from "./utils";
import type { OnboardingCaseType, WizardCaseInfo } from "../types";

type AccessCheckQuery = (args: {
  variables: {
    caseType: ComplianceCaseTypeEnum;
    externalObject: NonNullable<ReturnType<typeof buildLinkExternalObject>>;
  };
}) => Promise<{ error?: unknown }>;

export const isCreateBlockedByUnauthorizedArena = async (
  checkExistingCase: AccessCheckQuery,
  caseType: OnboardingCaseType,
  caseInfo: WizardCaseInfo,
): Promise<boolean> => {
  if (!isLinkedOnboardingSubject(caseType, caseInfo)) return false;
  // Workspace auth is on the arena for both CO and EO. Probing the entity
  // account does not surface the unauthorized-workspace error.
  const externalObject = buildLinkExternalObject({
    subjectKind: "client",
    clientId: caseInfo.clientId,
    clientName: caseInfo.clientName,
  });
  if (!externalObject) return false;
  try {
    const result = await checkExistingCase({
      variables: {
        caseType: ComplianceCaseTypeEnum.ClientOnboarding,
        externalObject,
      },
    });
    return isUnauthorizedWorkspaceError(result.error);
  } catch (error) {
    return isUnauthorizedWorkspaceError(error);
  }
};

export const useCreateCase = () => {
  const history = useHistory();
  const { caseInfo, caseType, newProfileData } = useEntityClientWizard();
  const [submitting, setSubmitting] = useState(false);

  const programsResult = useGetProgramNamesAndIdsQuery({ fetchPolicy: "cache-and-network" });
  const programNameToId = useMemo(() => {
    const entries =
      programsResult.data?.programsAssignableToEntities?.map((item): [string, number] => [
        item.name,
        item.id,
      ]) ?? [];
    return Object.fromEntries(entries);
  }, [programsResult.data]);

  const [checkExistingCase] = useOnboardingCaseForSubjectLazyQuery({
    fetchPolicy: "no-cache",
  });

  const [createCase] = useCreateEntityClientOnboardingCaseMutation({
    onError: (e: Error) => {
      toast.error(isUnauthorizedWorkspaceError(e) ? UNAUTHORIZED_WORKSPACE_TOAST : e.message);
    },
    onCompleted: async () => {
      await client.refetchQueries({ include: "active" });
    },
  });

  const goToCase = useCallback(
    (caseId?: number) => {
      if (!caseId) return;
      toast.success("New case created");
      history.push(`${globalEnv.complianceRoute}/case/${caseId}`);
    },
    [history],
  );

  const resolveProgramId = useCallback((): number | undefined => {
    if (!caseInfo) return undefined;
    if (caseInfo.programId) return caseInfo.programId;
    if (caseInfo.programName) return programNameToId[caseInfo.programName];
    return undefined;
  }, [caseInfo, programNameToId]);

  const handleCreateCase = useCallback(async () => {
    if (!caseInfo || !newProfileData) return;

    const programId = resolveProgramId();
    if (!programId) {
      const denied = await isCreateBlockedByUnauthorizedArena(
        checkExistingCase,
        caseType,
        caseInfo,
      );
      toast.error(
        denied ? UNAUTHORIZED_WORKSPACE_TOAST : missingProgramToastMessage(caseType, caseInfo),
      );
      return;
    }

    const externalArena = buildExternalArena(caseInfo);
    if (!externalArena) {
      toast.error("Case cannot be created because a client is required");
      return;
    }

    const onboardingCaseType = resolveOnboardingCaseType(caseType);
    const profileData = buildInstitutionProfileInput(newProfileData);
    const externalEntity = caseType === "entity" ? buildExternalEntity(caseInfo) : undefined;

    setSubmitting(true);
    try {
      const response = await createCase({
        variables: {
          profileData,
          programId,
          externalArena,
          caseType: onboardingCaseType,
          assignSelf: caseInfo.assignToMyself,
          documentIds: newProfileData.documentId ? [newProfileData.documentId] : null,
          ...(externalEntity ? { externalEntity } : {}),
        },
      });
      goToCase(response.data?.createCaseWithNewInstitutionProfile.case.id);
    } catch {
      // Mutation errors are surfaced via onError; catch prevents unhandled rejection.
    } finally {
      setSubmitting(false);
    }
  }, [
    caseInfo,
    caseType,
    newProfileData,
    resolveProgramId,
    checkExistingCase,
    createCase,
    goToCase,
  ]);

  return {
    handleCreateCase,
    submitting,
    programsLoading: programsResult.loading,
  };
};
